import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Clock, Guitar, ScanLine, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PublicHeader, PublicFooter } from '@/components/PublicShell';
import { StageStatusBadge, formatDatePt } from '@/components/Badges';

export default function StageDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [stage, setStage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    api.get(`/public/stages/${slug}`).then((r) => setStage(r.data)).catch(() => setError(true));
    api.get('/public/settings').then((r) => setSettings(r.data));
  }, [slug]);

  if (error) return <div>
    <PublicHeader />
    <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Etapa não encontrada.</div>
  </div>;
  if (!stage) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando…</div>;

  const finished = stage.status === 'finished';

  return (
    <div>
      <PublicHeader />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
        <Link to="/calendario" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4" /> Voltar ao calendário
        </Link>

        <div className="flex items-center gap-3 flex-wrap">
          <StageStatusBadge status={stage.status} />
          <div className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" /> {formatDatePt(stage.date)} • {stage.start_time || '21:00'}
          </div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-white mt-2 leading-none">{stage.title}</h1>

        {/* Battle card */}
        <Card className="mt-6 border border-border bg-card">
          <CardContent className="p-6">
            {stage.bands_count === 3 ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
                <BandTile band={stage.band_a} side="A" votes={finished ? stage.band_a_votes : null} pct={finished ? stage.band_a_percentage : null} winner={finished && stage.winner_band?.id === stage.band_a?.id} />
                <BandTile band={stage.band_b} side="B" votes={finished ? stage.band_b_votes : null} pct={finished ? stage.band_b_percentage : null} winner={finished && stage.winner_band?.id === stage.band_b?.id} />
                <BandTile band={stage.band_c} side="C" votes={finished ? stage.band_c_votes : null} pct={finished ? stage.band_c_percentage : null} winner={finished && stage.winner_band?.id === stage.band_c?.id} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
                <BandTile band={stage.band_a} side="A" votes={finished ? stage.band_a_votes : null} pct={finished ? stage.band_a_percentage : null} winner={finished && stage.winner_band?.id === stage.band_a?.id} />
                <div className="text-center font-display text-3xl text-[var(--g66-red)]">VS</div>
                <BandTile band={stage.band_b} side="B" votes={finished ? stage.band_b_votes : null} pct={finished ? stage.band_b_percentage : null} winner={finished && stage.winner_band?.id === stage.band_b?.id} />
              </div>
            )}

            {finished && (
              <div className="mt-6" data-testid="result-bar">
                <ResultBar
                  bands={[
                    { name: stage.band_a?.name, id: stage.band_a?.id, votes: stage.band_a_votes || 0, pct: stage.band_a_percentage || 0 },
                    { name: stage.band_b?.name, id: stage.band_b?.id, votes: stage.band_b_votes || 0, pct: stage.band_b_percentage || 0 },
                    ...(stage.bands_count === 3 && stage.band_c ? [{ name: stage.band_c?.name, id: stage.band_c?.id, votes: stage.band_c_votes || 0, pct: stage.band_c_percentage || 0 }] : []),
                  ]}
                  winnerId={stage.winner_band?.id}
                />
                <div className="mt-4 text-center text-sm text-muted-foreground" data-testid="result-bar-total-votes">
                  Total de votos válidos: <span className="text-white font-semibold">{stage.total_votes || 0}</span>
                </div>
              </div>
            )}

            {stage.status === 'voting_open' && (
              <div className="mt-6 space-y-4">
                <div className="text-center text-sm text-[var(--g66-amber)] bg-[color:hsla(42,92%,52%,0.10)] border border-[color:hsla(42,92%,52%,0.30)] rounded-lg p-4">
                  Votação ABERTA. Use o QR Code da sua comanda para votar. O placar parcial não é exibido publicamente.
                </div>

                <div className="rounded-lg border border-border bg-[var(--g66-surface)] p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <ScanLine className="w-4 h-4 text-[var(--g66-amber)]" />
                    <div className="font-semibold text-white">Não conseguiu ler o QR Code?</div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Digite o <strong className="text-white">código de 6 dígitos</strong> que está impresso na sua comanda (abaixo do QR Code).
                  </p>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const raw = manualCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
                      if (raw.length < 4) { toast.error('Digite o código de 6 dígitos da comanda.'); return; }
                      setValidating(true);
                      api.get(`/vote/lookup-short/${encodeURIComponent(stage.slug)}/${encodeURIComponent(raw)}`)
                        .then((r) => {
                          const data = r.data || {};
                          if (!data.found) {
                            const messages = {
                              short_code_invalid: 'Digite o código completo (6 dígitos).',
                              short_code_not_found: 'Código não encontrado nesta etapa. Confira os dígitos ou peça ajuda à equipe do Pub.',
                              cancelled: 'Esta comanda foi cancelada. Procure a equipe do Pub.',
                              stage_not_found: 'Etapa não encontrada.',
                            };
                            toast.error(messages[data.reason] || 'Código não encontrado.');
                            return;
                          }
                          if (data.status === 'used') {
                            toast.error('Esta comanda já foi utilizada para votar.');
                            return;
                          }
                          nav(`/votar/${data.token}`);
                        })
                        .catch(() => toast.error('Erro ao validar código. Tente novamente.'))
                        .finally(() => setValidating(false));
                    }}
                    className="flex flex-col sm:flex-row gap-2"
                  >
                    <Input
                      data-testid="manual-code-input"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                      placeholder="ABC123"
                      className="flex-1 bg-background font-mono uppercase tracking-[0.4em] text-center text-lg"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      inputMode="text"
                      maxLength={6}
                    />
                    <Button
                      type="submit"
                      data-testid="manual-code-submit"
                      disabled={validating || manualCode.trim().length < 4}
                      className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]"
                    >
                      {validating ? 'Validando…' : (<>Ir para votação <ArrowRight className="w-4 h-4 ml-1" /></>)}
                    </Button>
                  </form>
                </div>
              </div>
            )}
            {stage.status === 'scheduled' && (
              <div className="mt-6 text-center text-sm text-muted-foreground">
                Votação será aberta durante a apresentação.
              </div>
            )}
            {stage.status === 'voting_closed' && (
              <div className="mt-6 text-center text-sm text-muted-foreground">
                Votação encerrada. Aguardando apuração.
              </div>
            )}

            {finished && stage.winner_band && (
              <div className="mt-6 flex items-center justify-center gap-2 text-[var(--g66-amber)]" data-testid="stage-winner">
                <Trophy className="w-6 h-6" />
                <span className="font-display text-2xl">Vencedora: {stage.winner_band.name}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {stage.public_notes && (
          <Card className="mt-6 border border-border bg-card">
            <CardContent className="p-5">
              <div className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Observações</div>
              <div className="text-white/90 whitespace-pre-line">{stage.public_notes}</div>
            </CardContent>
          </Card>
        )}
      </div>
      <PublicFooter settings={settings} />
    </div>
  );
}

function BandTile({ band, side, votes, pct, winner }) {
  return (
    <div className={`rounded-xl border p-5 ${winner ? 'border-[color:hsla(42,92%,52%,0.55)] bg-[color:hsla(42,92%,52%,0.06)]' : 'border-border bg-[var(--g66-surface)]'}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Banda {side}</div>
      <div className="mt-1 flex items-center gap-3">
        <div className="w-14 h-14 rounded-lg bg-[#0b0c0f] flex items-center justify-center border border-border overflow-hidden">
          {band?.logo_url ? <img src={band.logo_url} alt={band.name} className="w-full h-full object-cover" /> : <Guitar className="w-6 h-6 text-muted-foreground" />}
        </div>
        <div className="min-w-0">
          <div className="font-display text-2xl text-white truncate">{band?.name || 'A definir'}</div>
          <div className="text-xs text-muted-foreground truncate">{band?.city}</div>
        </div>
      </div>
      {votes != null && (
        <div className="mt-3 flex items-baseline justify-between">
          <div className="font-display text-4xl text-white">{votes}</div>
          <div className="text-lg text-[var(--g66-amber)]">{pct?.toFixed?.(1) || 0}%</div>
        </div>
      )}
    </div>
  );
}

function ResultBar({ bands, winnerId }) {
  return (
    <div className="space-y-3">
      {bands.map((b, i) => (
        <BarRow
          key={b.id || i}
          name={b.name}
          pct={b.pct}
          votes={b.votes}
          isWinner={winnerId && winnerId === b.id}
          testId={`result-bar-band-${['a', 'b', 'c'][i]}`}
        />
      ))}
    </div>
  );
}
function BarRow({ name, pct, votes, isWinner, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-sm">
        <span className={isWinner ? 'text-[var(--g66-amber)] font-semibold' : 'text-white'}>{name}</span>
        <span className="text-muted-foreground">{votes} votos • {pct?.toFixed?.(1) || 0}%</span>
      </div>
      <div className="h-3 rounded-full bg-[#0b0c0f] overflow-hidden border border-border mt-1">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct || 0}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className={`h-full ${isWinner ? 'bg-[var(--g66-amber)]' : 'bg-[var(--g66-red)]'}`}
        />
      </div>
    </div>
  );
}
